import { Avatar, Box, Button, ButtonBase, Card, CardContent, Stack, Typography } from "@mui/material";
import AddRounded from "@mui/icons-material/AddRounded";
import { cardStyle } from "./busDetails/detailLayout";

// Dashboard palette: one primary accent, status colours only where they carry meaning.
export const DASHBOARD_COLORS = {
  navy: "#123047",
  primary: "#2563EB",
  primaryHover: "#1D4ED8",
  primarySoft: "#EFF6FF",
  page: "#F5F7FA",
  border: "#E2E8F0",
  muted: "#64748B",
};

export const STATUS_COLORS = {
  pending: { label: "Pending", color: "#D97706" },
  approved: { label: "Approved", color: "#16A34A" },
  rejected: { label: "Rejected", color: "#DC2626" },
};

export const hasCount = (value) => value !== undefined && value !== null && value !== "";

export const focusRing = { "&:focus-visible": { outline: `2px solid ${DASHBOARD_COLORS.primary}`, outlineOffset: 2 } };

export const statusDot = (color) => ({ width: 8, height: 8, borderRadius: "50%", bgcolor: color, flexShrink: 0 });

// Category icon with a small plus badge, so each Add button shows what it adds.
function AddIcon({ Icon }) {
  return (
    <Box component="span" sx={{ position: "relative", display: "inline-flex" }}>
      <Icon sx={{ fontSize: 20 }} />
      <Box component="span" sx={{ position: "absolute", right: -5, bottom: -4, width: 12, height: 12, borderRadius: "50%", bgcolor: "#fff", color: DASHBOARD_COLORS.primary, display: "inline-flex", alignItems: "center", justifyContent: "center" }}>
        <AddRounded sx={{ fontSize: 11 }} />
      </Box>
    </Box>
  );
}

const statusPanel = { bgcolor: "#F8FAFC", border: `1px solid ${DASHBOARD_COLORS.border}`, borderRadius: "10px", px: 1.25, py: 0.5 };

// Counts are shown exactly as the dashboard API returns them.
const DashboardCard = ({ title, total, pending, approved, rejected, Icon, onOpen, addLabel, onAdd }) => {
  const counts = { pending, approved, rejected };
  const tracked = Object.values(counts).some(hasCount);
  const header = (
    <Stack direction="row" alignItems="center" spacing={1.5} sx={{ width: "100%" }}>
      <Avatar variant="rounded" sx={{ bgcolor: DASHBOARD_COLORS.primarySoft, color: DASHBOARD_COLORS.primary, width: 46, height: 46, borderRadius: "12px" }}>
        <Icon />
      </Avatar>
      <Box sx={{ minWidth: 0, textAlign: "left" }}>
        <Typography sx={{ fontSize: 13, fontWeight: 600, lineHeight: 1.3, color: DASHBOARD_COLORS.muted }}>{title}</Typography>
        <Typography component="p" fontWeight={800} sx={{ fontSize: 28, lineHeight: 1.15, color: DASHBOARD_COLORS.navy, letterSpacing: "-0.01em" }}>{total ?? 0}</Typography>
      </Box>
    </Stack>
  );

  return (
    <Card sx={{ ...cardStyle, borderColor: DASHBOARD_COLORS.border, borderRadius: "14px" }}>
      <CardContent sx={{ p: 2, "&:last-child": { pb: 2 }, height: "100%", display: "flex", flexDirection: "column", gap: 1.75 }}>
        {onOpen ? (
          <ButtonBase onClick={onOpen} aria-label={`Open ${title} list`} sx={{ borderRadius: "10px", justifyContent: "flex-start", ...focusRing }}>{header}</ButtonBase>
        ) : header}

        {tracked ? (
          <Box sx={statusPanel}>
            {Object.entries(counts).map(([key, value]) => {
              const { label, color } = STATUS_COLORS[key];
              return (
                <Stack key={key} direction="row" alignItems="center" spacing={1} sx={{ minHeight: 28, "& + &": { borderTop: `1px dashed ${DASHBOARD_COLORS.border}` } }}>
                  <Box component="span" sx={statusDot(color)} />
                  <Typography sx={{ flex: 1, fontSize: 13, fontWeight: 500, color: DASHBOARD_COLORS.muted, lineHeight: 1.3 }}>{label}</Typography>
                  <Typography sx={{ fontSize: 14, fontWeight: 700, color: DASHBOARD_COLORS.navy, lineHeight: 1.3 }}>{value ?? 0}</Typography>
                </Stack>
              );
            })}
          </Box>
        ) : (
          <Box sx={{ ...statusPanel, py: 1, display: "flex", alignItems: "center", minHeight: 48 }}>
            <Typography sx={{ fontSize: 13, color: DASHBOARD_COLORS.muted, lineHeight: 1.35 }}>No verification status for this category</Typography>
          </Box>
        )}

        {onAdd && (
          <Button variant="contained" disableElevation fullWidth startIcon={<AddIcon Icon={Icon} />} onClick={onAdd}
            sx={{
              mt: "auto", height: 40, px: 2, borderRadius: "10px", textTransform: "none", fontWeight: 600, fontSize: 14, letterSpacing: 0,
              color: "#fff !important", bgcolor: DASHBOARD_COLORS.primary, boxShadow: "0 1px 2px rgba(18,48,71,.12)",
              transition: "background-color .2s ease, box-shadow .2s ease, transform .2s ease",
              "& .MuiButton-startIcon": { mr: 1.25, ml: 0 },
              "&:hover": { bgcolor: DASHBOARD_COLORS.primaryHover, boxShadow: "0 4px 12px rgba(37,99,235,.25)" },
              "&:active": { transform: "translateY(1px)" },
              ...focusRing,
            }}>
            {addLabel}
          </Button>
        )}
      </CardContent>
    </Card>
  );
};

export default DashboardCard;
